import {
  getTabSession,
  hasTabSession,
  removeTabSession,
  setTabSession,
  updateTabSession,
} from "@/utils/tabSession";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

type UseTabSessionOptions = {
  namespace: string;
  version?: number;
};

const useTabSession = <T>({ namespace, version = 1 }: UseTabSessionOptions) => {
  const read = useCallback((): T | null => {
    return getTabSession<T>(namespace, version);
  }, [namespace, version]);

  const write = useCallback(
    (data: T): boolean => {
      return setTabSession<T>(namespace, data, version);
    },
    [namespace, version],
  );

  const update = useCallback(
    (
      partial: T extends Record<string, unknown> ? Partial<T> : never,
    ): boolean => {
      return updateTabSession(
        namespace,
        partial as Partial<Record<string, unknown>>,
        version,
      );
    },
    [namespace, version],
  );

  const clear = useCallback(() => {
    removeTabSession(namespace);
  }, [namespace]);

  const has = useCallback((): boolean => {
    return hasTabSession(namespace, version);
  }, [namespace, version]);

  return { read, write, update, clear, has };
};

export const useTabSessionState = <T>(
  namespace: string,
  initialValue: T,
  version = 1,
): [T, Dispatch<SetStateAction<T>>] => {
  const [state, setState] = useState<T>(initialValue);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const stored = getTabSession<T>(namespace, version);
    if (stored !== null) {
      setState(stored);
    }
    setIsReady(true);
  }, [namespace, version]);

  useEffect(() => {
    if (!isReady) return;
    setTabSession(namespace, state, version);
  }, [isReady, namespace, state, version]);

  return [state, setState];
};

export default useTabSession;
